import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

function ShieldIcon({ color, size = 64 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M32 4L8 14V30C8 43.25 18.4 55.74 32 59C45.6 55.74 56 43.25 56 30V14L32 4Z"
                fill={hexToRgba(color, 0.15)}
                stroke={color}
                strokeWidth="2"
                strokeLinejoin="round"
            />
            <path
                d="M22 32l6 6 14-14"
                stroke={color}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function CheckIcon({ color }) {
    return (
        <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    );
}

const DEFAULT_GUARANTEES = [
    { icon: '🛡️', title: 'Sin riesgos', description: 'Prueba sin compromisos durante 30 días completos.' },
    { icon: '💰', title: 'Reembolso total', description: 'Si no estás satisfecho, te devolvemos cada centavo.' },
    { icon: '🔒', title: 'Datos seguros', description: 'Tu información está protegida con encriptación de nivel bancario.' },
];

export default function GuaranteeSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'centered';

    const accent = s.accent || primaryColor || '#10b981';
    const guarantees = content.guarantees && content.guarantees.length > 0 ? content.guarantees : DEFAULT_GUARANTEES;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Centered: single big guarantee with shield ──
    if (variant === 'centered') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="text-center max-w-2xl mx-auto py-8">
                        {/* Glassmorphism badge */}
                        <div
                            className="inline-flex flex-col items-center gap-4 px-10 py-10 rounded-3xl mb-8"
                            style={{
                                background: s.dark
                                    ? `linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))`
                                    : `linear-gradient(135deg, ${hexToRgba(accent, 0.07)}, ${hexToRgba(accent, 0.03)})`,
                                border: `1.5px solid ${hexToRgba(accent, 0.25)}`,
                                backdropFilter: 'blur(12px)',
                                boxShadow: `0 8px 32px ${hexToRgba(accent, 0.12)}`,
                            }}
                        >
                            <ShieldIcon color={accent} size={72} />
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black mb-2" style={{ color: s.text }}>
                                    {content.title || '100% Satisfacción Garantizada'}
                                </h3>
                                {content.description && (
                                    <p className="text-sm md:text-base leading-relaxed" style={{ color: s.textSub }}>
                                        {content.description}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* List of sub-guarantees */}
                        {guarantees.length > 0 && (
                            <div className="flex flex-col gap-3 text-left max-w-md mx-auto">
                                {guarantees.map((g, i) => (
                                    <div
                                        key={i}
                                        className="flex items-start gap-3 px-4 py-3 rounded-xl"
                                        style={{
                                            opacity: visible ? 1 : 0,
                                            transform: visible ? 'translateY(0)' : 'translateY(16px)',
                                            transition: `opacity 0.5s ease ${i * 100 + 200}ms, transform 0.5s ease ${i * 100 + 200}ms`,
                                        }}
                                    >
                                        <CheckIcon color={accent} />
                                        <div>
                                            <span className="font-semibold text-sm" style={{ color: s.text }}>{g.title}</span>
                                            {g.description && <p className="text-xs mt-0.5 leading-relaxed" style={{ color: s.textSub }}>{g.description}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards: grid of guarantee cards ──
    if (variant === 'cards') {
        const cols = guarantees.length === 2 ? 'md:grid-cols-2' : guarantees.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || '100% Satisfacción Garantizada'} subtitle={content.subtitle || content.description} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {guarantees.map((g, i) => (
                            <div
                                key={i}
                                className="group relative rounded-2xl p-6 text-center transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                                style={{
                                    background: s.dark
                                        ? `rgba(255,255,255,0.04)`
                                        : `linear-gradient(135deg, ${hexToRgba(accent, 0.05)}, ${hexToRgba(accent, 0.02)})`,
                                    border: `1.5px solid ${hexToRgba(accent, 0.18)}`,
                                    backdropFilter: 'blur(8px)',
                                    opacity: visible ? 1 : 0,
                                    transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                    transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s ease ${i * 120}ms, box-shadow 0.3s ease`,
                                }}
                            >
                                {/* Icon or Shield */}
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                                    style={{ backgroundColor: hexToRgba(accent, 0.12), border: `1px solid ${hexToRgba(accent, 0.25)}` }}
                                >
                                    {g.icon ? (
                                        <span className="text-3xl">{g.icon}</span>
                                    ) : (
                                        <ShieldIcon color={accent} size={32} />
                                    )}
                                </div>
                                <h3 className="font-bold text-lg mb-2" style={{ color: s.text }}>{g.title}</h3>
                                {g.description && (
                                    <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{g.description}</p>
                                )}
                                {/* Accent line */}
                                <div
                                    className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-500 group-hover:w-3/4"
                                    style={{ width: '40%', backgroundColor: accent, opacity: 0.5 }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Banner: horizontal strip ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div
                    className="rounded-2xl px-6 md:px-10 py-8 flex flex-col md:flex-row items-center gap-6 md:gap-10"
                    style={{
                        background: s.dark
                            ? `linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))`
                            : `linear-gradient(135deg, ${hexToRgba(accent, 0.08)}, ${hexToRgba(accent, 0.03)})`,
                        border: `1.5px solid ${hexToRgba(accent, 0.2)}`,
                        boxShadow: `0 8px 32px ${hexToRgba(accent, 0.1)}`,
                    }}
                >
                    {/* Shield */}
                    <div className="shrink-0">
                        <ShieldIcon color={accent} size={64} />
                    </div>
                    {/* Center text */}
                    <div className="flex-1 text-center md:text-left">
                        <h3 className="text-xl md:text-2xl font-black mb-1" style={{ color: s.text }}>
                            {content.title || '100% Satisfacción Garantizada'}
                        </h3>
                        {content.description && (
                            <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{content.description}</p>
                        )}
                    </div>
                    {/* Guarantees inline */}
                    {guarantees.length > 0 && (
                        <div className="flex flex-col gap-2 shrink-0">
                            {guarantees.map((g, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <CheckIcon color={accent} />
                                    <span className="text-sm font-medium" style={{ color: s.text }}>{g.title}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
